import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import {
  findLanguageUseSchemesForTask,
  getSoloLanguageUseRule,
  taskGrammarMode,
  type LanguageUseSchemeLike,
} from '../../../shared/grammar-rules-resolution.js';
import { BoundingBox } from '../../BoundingBox.js';
import { Exam, type ExamTask } from '../../exam.js';
import { StepState } from '../../extraction-progress.js';
import { Submission, TaskGradingState, type SubmissionTask } from '../../submission.js';
import type { Grading } from '../../utils/grading-utils.js';
import { ownMaxPoints } from '../../utils/points-model.js';
import {
  appendTaskRefSegment,
  cumulativeTaskRefs,
  normalizeTaskRef,
  normalizeTaskRefSegment,
  toNormalizedTaskRefSet,
} from '../../utils/task-ref.js';
import { upsertLanguageUseRuleResult } from '../language-use-rule-result.js';
import { persistGrammarFiOutcomes } from '../persist-grammar-fi-outcomes.js';
import { type ConsistencyReviewInput, gradeTask, type GradeTaskTarget } from './default-grading.js';
import {
  GRAMMAR_TASK_TYPES,
  detectGrammarErrorsForTask,
  locateGrammarErrorsForTask,
  type DetectedGrammarError,
  type RunGrammarEvaluationArgs,
} from './grammar-evaluation.js';
import { hasGradingSchemeContent, hasMasterSolutionContent } from './tree-generation-utils.js';

/**
 * How much of a student's answer is kept in a row's stored consistency items.
 *
 * Generous against the 500 the prompt slices to, so a later change there has room, and small enough that a
 * cohort's worth of rows is kilobytes rather than tens of megabytes — these are read back in bulk by the per-task
 * reconciliation and by the wave's announcement.
 */
const STORED_ANSWER_EXCERPT_LIMIT = 1000;

const clampScore = (score: number, maxPoints: number) => {
  if (!Number.isFinite(score)) return 0;
  return Math.max(0, Math.min(score, maxPoints));
};

/**
 * Runs `thunks` in waves of `GRADING_WAVE`, settling each wave before the next starts. Never
 * rejects — a failing thunk is that thunk's business, exactly as `Promise.allSettled` was at these call sites.
 *
 * THUNKS AND NOT PROMISES is the whole reason this exists. `Promise.all`/`allSettled` over an array of already-
 * started promises bounds nothing at all: every branch is running before the combinator is even called, so a
 * cohort-sized array holds a cohort's worth of model payloads. The bound has to be on when a branch STARTS.
 */
/**
 * HOW MANY MODEL PAYLOADS THIS AGENT KEEPS IN FLIGHT, and it is a local constant on purpose.
 *
 * It used to be `GRADING_WAVE`, derived from a per-job heap budget — a wave width computed from
 * `(budget - baseline) / payload`. That budget is gone (see the header of `jobs/load.ts`), and the width should
 * never have depended on it anyway: what this bounds is how many submission payloads this agent assembles at once,
 * which is a property of the agent, not of a scheduler's arithmetic.
 *
 * Eight because a grading payload is one submission's answers for one task and the model gate holds thirty batch
 * permits — wide enough to keep the provider busy, narrow enough that a cohort is not resident at once.
 */
const GRADING_WAVE = 8;

async function inWaves(thunks: Array<() => Promise<unknown>>): Promise<void> {
  for (let from = 0; from < thunks.length; from += GRADING_WAVE) {
    const wave = thunks.slice(from, from + GRADING_WAVE);
    // eslint-disable-next-line no-await-in-loop -- sequential waves ARE the bound; that is the point
    await Promise.allSettled(wave.map(run => run()));
  }
}

/** Named fields on GradingResult, used to preserve them on a scoped regrade that doesn't recompute content. */
const CONTENT_RESULT_FIELDS = [
  'score',
  'explanation',
  'confidence',
  'reviewerFeedback',
  'actualPointsGiven',
  'isApproved',
  'gradingSource',
] as const;

function pickFields(source: any, fields: readonly string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    if (source && source[field] !== undefined) out[field] = source[field];
  }
  return out;
}

/**
 * Merges a freshly-computed GradingResult into whatever the existing document already had. The write
 * is always a full-object $set (Mongo doesn't merge nested docs), so untouched fields must be
 * explicitly carried over from `existing` or a content-only regrade would silently wipe them. Pass
 * `null` when this particular result doesn't carry new content data (language-use-only calls) — preserves
 * the existing content untouched. Language-use results never live here — see queueSoloLanguageUseWrite.
 */
function buildMergedGradingResult(
  existing: any,
  freshContent: Record<string, unknown> | null,
): Record<string, unknown> {
  const content = freshContent ?? pickFields(existing, CONTENT_RESULT_FIELDS);
  return { _class: 'GradingResult', ...content };
}

/**
 * Writes a solo (1-member) LanguageUseScheme's result into Submission.languageUseRuleResults — the same
 * array/upsert a multi-member group's result lands in, keyed by languageUseSchemeId. Solo grading itself
 * stays batched into the content LLM call (see default-grading.ts); only the write destination is
 * unified here, non-fatal like the group write, so a language-use-write failure never blocks the content
 * $set that happens alongside it.
 */
function queueSoloLanguageUseWrite(
  pending: Promise<unknown>[],
  submissionId: string,
  grading: any,
  examID: string,
): void {
  if (!grading || grading.languageUseSchemeId === undefined || grading.languageUseGrade === undefined) return;
  pending.push(
    upsertLanguageUseRuleResult(submissionId, grading.languageUseSchemeId, {
      score: grading.languageUseGrade,
      explanation: grading.languageUseExplanation,
      confidence: grading.languageUseConfidence,
      reviewerFeedback: grading.languageUseReviewerFeedback,
    }).catch(err =>
      logger.error(
        { err, examID, submissionId, schemeId: grading.languageUseSchemeId, context: 'LANGUAGE_USE' },
        'Solo language-use result write failed (non-fatal)',
      ),
    ),
  );
}

// Reactive grading: how long to keep waiting for a task's master solution / grading scheme to land
// before grading it anyway, and how often to re-check. The timeout is a safety valve so a caller
// awaiting completion never hangs on content that never arrives.
//
// Sized for the supported "generate what is missing, then grade" chain: the generation is detached and
// still running when grading starts, and it needs one LLM call per top-level task, so a whole exam takes
// minutes rather than seconds. At 60s the deadline was reached mid-generation on any exam bigger than a
// couple of tasks, and since hitting it FORCES the remaining tasks to be graded (see `force` in the loop
// below), those were graded against no master solution at all.
//
// Note this only reduces the chance of that, it does not remove it: an exam whose generation outlives the
// deadline still force-grades the rest. The loop is also only entered while something is genuinely
// missing — when the solutions are already in place the first pass grades everything and breaks, so the
// longer window costs nothing in the normal case.
const GRADING_WAIT_TIMEOUT_MS = 10 * 60 * 1000;
const GRADING_WAIT_POLL_MS = 2500;

const findTaskByPath = (task: any, path: string[]): any | undefined => {
  let current = task;

  for (const segment of path) {
    const subTasks = Array.isArray(current?.subTasks) ? current.subTasks : [];
    current = subTasks.find((subTask: any) => String(subTask?.taskNumber) === String(segment));
    if (!current) return undefined;
  }

  return current;
};

/** Same traversal as findTaskByPath, but over a submission's subTasksSubmission tree. */
const findSubmissionNodeByPath = (submissionTask: any, path: string[]): any | undefined => {
  let current = submissionTask;

  for (const segment of path) {
    const subTasks = Array.isArray(current?.subTasksSubmission) ? current.subTasksSubmission : [];
    current = subTasks.find((subTask: any) => String(subTask?.taskNumber) === String(segment));
    if (!current) return undefined;
  }

  return current;
};

/**
 * Build MongoDB update path for nested tasks.
 * E.g., subTasks ["a", "(I)"] becomes:
 * { path: "taskSubmissions.$[main].subTasksSubmission.$[sub0].subTasksSubmission.$[sub1].gradingResult",
 *   filters: [{"main.taskNumber": "Aufgabe 1"}, {"sub0.taskNumber": "a"}, {"sub1.taskNumber": "(I)"}] }
 */
const buildNestedUpdatePath = (
  subTasks: string[],
  filterPrefix: string,
): { path: string; filters: Record<string, any>[] } => {
  const filters: Record<string, any>[] = [];
  let path = 'taskSubmissions.$[main]';

  for (let i = 0; i < subTasks.length; i++) {
    const filterName = `${filterPrefix}${i}`;
    path += `.subTasksSubmission.$[${filterName}]`;
    filters.push({ [`${filterName}.taskNumber`]: subTasks[i] });
  }

  return { path, filters };
};

interface GrammarTarget {
  examNode: any;
  submissionNode: SubmissionTask;
  fieldPath: string;
  arrayFilters: Record<string, unknown>[];
  /** This node's "|"-joined ancestor path (e.g. "2|a") — the same qualified reference
   * LanguageUseScheme.dependsOn now stores, so a bare local taskNumber (ambiguous whenever it's reused
   * as a subtask label under more than one parent) is never used to match against it. */
  qualifiedTaskNumber: string;
}

/**
 * Walk an exam task's own node plus every descendant subtask, pairing each with its matching
 * submission node, and collect every node that needs a grammar/spelling evaluation of SOME kind
 * (this feeds the real LanguageTool detection pipeline below, regardless of whether the node ends up
 * scored via FI or via a rule — see `taskGrammarMode` for that later decision): either its OWN taskType
 * is in GRAMMAR_TASK_TYPES, or it belongs to a LanguageUseScheme at all — solo (one `dependsOn` entry) or
 * multi-member, which is now the only way such a rule is represented — regardless of taskType. A combined
 * top-level task can be tagged a purely structural type (e.g. TASK_FRAME) while its subtasks carry the real
 * essay/poetry/etc type, so eligibility must be checked at every depth, not just the top.
 */
const collectGrammarTargets = (
  examNode: any,
  submissionNode: SubmissionTask | undefined,
  relativePath: string[],
  mainTaskNumber: string,
  languageUseSchemeRules: LanguageUseSchemeLike[],
): GrammarTarget[] => {
  if (!submissionNode) return [];
  const targets: GrammarTarget[] = [];

  const qualifiedTaskNumber = [mainTaskNumber, ...relativePath].join('|');
  const isEligible =
    GRAMMAR_TASK_TYPES.has(String(examNode?.taskType)) ||
    findLanguageUseSchemesForTask(qualifiedTaskNumber, languageUseSchemeRules).length > 0;

  if (isEligible) {
    const { path, filters } = buildNestedUpdatePath(relativePath, 'g');
    targets.push({
      examNode,
      submissionNode,
      fieldPath: `${path}.grammarEvaluation`,
      arrayFilters: [{ 'main.taskNumber': mainTaskNumber }, ...filters],
      qualifiedTaskNumber,
    });
  }

  const examChildren = Array.isArray(examNode?.subTasks) ? examNode.subTasks : [];
  const submissionChildren: SubmissionTask[] = Array.isArray(submissionNode?.subTasksSubmission)
    ? (submissionNode.subTasksSubmission as SubmissionTask[])
    : [];
  examChildren.forEach((childExam: any, idx: number) => {
    const childSubmission =
      submissionChildren.find(c => String((c as any)?.taskNumber ?? '') === String(childExam?.taskNumber ?? '')) ??
      submissionChildren[idx];
    targets.push(
      ...collectGrammarTargets(
        childExam,
        childSubmission,
        [...relativePath, String(childExam?.taskNumber ?? idx)],
        mainTaskNumber,
        languageUseSchemeRules,
      ),
    );
  });

  return targets;
};

/**
 * Whether a "|"-joined qualified path (e.g. "1|1|a") — or any of its ancestors — is present in
 * requestedTaskRefs. Mirrors default-grading.ts's shouldGradeByFilter ancestor/descendant check:
 * a full grading run requests bare main-task refs ("1", "2", "3"), while a LanguageUseScheme's dependsOn
 * often names only nested descendants ("1|1", "1|1|a") that never literally equal one of those bare
 * refs. Without this, a group made entirely of nested members (no bare top-level member) is silently
 * dropped from every main task's target list and never graded at all.
 */
function qualifiedPathMatchesRequestedRefs(
  qualifiedTaskNumber: string,
  requestedTaskRefs: Set<string> | null,
): boolean {
  if (requestedTaskRefs == null) return true;
  // The walk itself is `cumulativeTaskRefs`, shared with the canvas so the box that shows a spinner is exactly
  // the node this decides to grade — see its doc.
  return cumulativeTaskRefs(qualifiedTaskNumber).some(ref => requestedTaskRefs.has(ref));
}

/** Recursive "|"-joined-qualified-path -> node lookup, used to resolve a LanguageUseScheme group's
 * members (dependsOn now stores that same qualified path — see build-exam-tasks.ts's
 * collectAllTaskNumbers — since a bare local taskNumber like "a" is ambiguous whenever it's reused
 * as a subtask label under more than one parent). */
export function collectNodesByTaskNumber<
  T extends { taskNumber?: unknown; subTasks?: unknown; subTasksSubmission?: unknown },
>(
  nodes: T[] | null | undefined,
  childKey: 'subTasks' | 'subTasksSubmission',
  parentPath: string[] = [],
  map = new Map<string, T>(),
): Map<string, T> {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    const tn = String((node as any)?.taskNumber ?? '');
    const path = tn ? [...parentPath, tn] : parentPath;
    if (tn) map.set(path.join('|'), node);
    collectNodesByTaskNumber((node as any)?.[childKey], childKey, path, map);
  }
  return map;
}

/**
 * Which distinct LanguageUseScheme groups this exam has — one grading call per (submission, group) regardless
 * of how many member tasks it depends on, so this is both what the grading-progress total counts per submission
 * and what `languageUseGroupGrading` dispatches a row for.
 *
 * `requestedTaskRefs` scopes it to a regrade's own refs, matched against the scheme's MEMBERS rather than
 * against the main tasks they sit under: two groups can hang off the same main task, and regrading one of them
 * must not sweep in the other. Null (the default) means the whole exam, which is what the progress total wants.
 */
export function relevantLanguageUseGroupIds(
  examTasks: any[],
  languageUseSchemeRules: LanguageUseSchemeLike[],
  requestedTaskRefs: Set<string> | null = null,
): string[] {
  const taskNumbers = collectNodesByTaskNumber(examTasks, 'subTasks');
  // Solo (1-member) rules are graded inline with the task's content call (see the `soloRule` branch
  // below), not via a dedicated gradeLanguageUseSchemeGroup call, so they don't add a progress unit here —
  // they're already counted in relevantExamTasks.length.
  return languageUseSchemeRules
    .filter(
      scheme =>
        Array.isArray(scheme?.dependsOn) &&
        scheme.dependsOn.length > 1 &&
        scheme.dependsOn.some(
          tn => taskNumbers.has(String(tn)) && qualifiedPathMatchesRequestedRefs(String(tn), requestedTaskRefs),
        ),
    )
    .map(scheme => (scheme as { _id?: unknown })._id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/** How many of them there are. The exam-wide `total` only needs the count; the unit ids need the list. */
function countRelevantLanguageUseGroups(examTasks: any[], languageUseSchemeRules: LanguageUseSchemeLike[]): number {
  return relevantLanguageUseGroupIds(examTasks, languageUseSchemeRules).length;
}

const buildSubmissionTaskSelection = (depth: number): any => {
  const selection: any = {
    _id: 1,
    _class: 1,
    taskNumber: 1,
    studentAnswer: 1,
    gradingResult: {
      score: 1,
      explanation: 1,
      actualPointsGiven: 1,
      confidence: 1,
      isApproved: 1,
      reviewerFeedback: 1,
      gradingSource: 1,
    },
    boundingBoxes: selectionSet(() => BoundingBox, 2),
    // Needed by the grammar reuse guard (a COMPLETED evaluation is kept as-is on regrade) and to
    // feed the stored error list into the grading prompt in rules modes.
    grammarEvaluation: {
      status: 1,
      errorCount: 1,
      wordCount: 1,
      fehlerindex: 1,
      errors: {
        _id: 1,
        offset: 1,
        length: 1,
        message: 1,
        shortMessage: 1,
        ruleId: 1,
        category: 1,
        context: 1,
      },
    },
  };

  if (depth > 0) {
    selection.subTasksSubmission = buildSubmissionTaskSelection(depth - 1);
  }

  return selection;
};

const buildExamTaskSelection = (depth: number): any => {
  const selection: any = {
    _id: 1,
    taskId: 1,
    taskNumber: 1,
    description: 1,
    taskType: 1,
    needsAnswer: 1,
    additionalInfoIds: 1,
    boundingBoxes: selectionSet(() => BoundingBox, 2),
    masterSolution: {
      examId: 1,
      taskId: 1,
      content: 1,
      feedback: 1,
      boundingBoxes: selectionSet(() => BoundingBox, 2),
    },
    gradingScheme: {
      examId: 1,
      criteria: 1,
      maxPoints: 1,
      feedback: 1,
      boundingBoxes: selectionSet(() => BoundingBox, 2),
    },
  };

  if (depth > 0) {
    selection.subTasks = buildExamTaskSelection(depth - 1);
  }

  return selection;
};

/**
 * Every node whose ref was requested, as positional `$set` paths plus the `arrayFilters` they need.
 *
 * WHY NOT REBUILD THE ARRAY. This used to construct a new `taskSubmissions` and `$set` the whole thing, which is a
 * read-modify-write: every concurrent writer into the same submission is silently reverted to whatever this
 * function happened to read. There are three, and none of them can be excluded by a lock — a sibling task's
 * grading row (the submission lock is per task now), the fire-and-forget grammar work that deliberately outlives
 * its job's lease (`inWaves(pendingGrammarWork)` below), and a teacher's own edit. Only `gradingState` was ever
 * changed, so only `gradingState` is written.
 *
 * One filter identifier per node on a matched branch, ancestors included: an ancestor's `$[id]` appears in its
 * matched descendant's path, and Mongo rejects a duplicate identifier as well as one declared but never used.
 * Identity is `taskNumber`, so sibling collisions over-match — exactly as the whole-task path below and the grade
 * write itself already do.
 */
function collectPendingTargets(
  taskSubmissions: any[],
  requestedRefs: Set<string>,
): { updateSet: Record<string, unknown>; arrayFilters: Array<Record<string, unknown>> } {
  const updateSet: Record<string, unknown> = {};
  const filtersById = new Map<string, Record<string, unknown>>();
  let nextId = 0;

  const walk = (
    nodes: any[],
    parentPath: string,
    ancestors: Array<[string, Record<string, unknown>]>,
    accumulatedRef: string,
  ): void => {
    for (const node of nodes) {
      const taskRef = normalizeTaskRefSegment(node?.taskNumber);
      const combinedRef = appendTaskRefSegment(accumulatedRef, taskRef);
      const id = `p${nextId++}`;
      const path = `${parentPath}.$[${id}]`;
      const own: [string, Record<string, unknown>] = [id, { [`${id}.taskNumber`]: String(node?.taskNumber ?? '') }];

      if (requestedRefs.has(combinedRef)) {
        updateSet[`${path}.gradingState`] = TaskGradingState.PENDING;
        for (const [filterId, filter] of [...ancestors, own]) filtersById.set(filterId, filter);
      }

      const children = Array.isArray(node?.subTasksSubmission) ? node.subTasksSubmission : [];
      if (children.length > 0) walk(children, `${path}.subTasksSubmission`, [...ancestors, own], combinedRef);
    }
  };

  walk(Array.isArray(taskSubmissions) ? taskSubmissions : [], 'taskSubmissions', [], '');
  return { updateSet, arrayFilters: [...filtersById.values()] };
}

/**
 * Whether this task, or anything under it, was requested — matched on the PATH-QUALIFIED ref only.
 *
 * A node's own bare label used to match here too, so a request for "b" selected every task with a "b"
 * somewhere under it. `gradeTask`'s own filter (default-grading's `shouldGradeByFilter`) never accepted
 * that, and the two run over the same ref set: dispatch queued jobs and marked nodes PENDING, grading
 * then matched nothing, and the run closed COMPLETED without writing a grade. Refs are resolved to a
 * full path at the tool boundary now (grading-agent.tool.ts), so this only has to agree with the grader.
 */
function taskMatchesAnyRequestedRef(task: any, requestedRefs: Set<string>, accumulatedRef: string): boolean {
  const taskRef = normalizeTaskRefSegment(task?.taskNumber);
  const combinedRef = appendTaskRefSegment(accumulatedRef, taskRef);
  if (requestedRefs.has(combinedRef)) return true;
  const subTasks = Array.isArray(task?.subTasks) ? task.subTasks : [];
  return subTasks.some((sub: any) => taskMatchesAnyRequestedRef(sub, requestedRefs, combinedRef));
}

async function resolveRelevantMainTaskNumbers(examID: string, taskNumbers?: string[]): Promise<string[]> {
  const examCol = await getCollection(Exam);
  const examDoc = await examCol.findOne({ _id: examID }, { projection: { tasks: 1 } });
  const examTasks = Array.isArray((examDoc as any)?.tasks) ? (examDoc as any).tasks : [];

  const requestedTaskRefs = taskNumbers && taskNumbers.length > 0 ? toNormalizedTaskRefSet(taskNumbers) : null;
  if (!requestedTaskRefs || requestedTaskRefs.size === 0) {
    return examTasks
      .map((task: any) => String(task?.taskNumber ?? ''))
      .filter((taskNumber: string) => taskNumber.length > 0);
  }

  const relevantExamTasks = examTasks.filter((task: any) => taskMatchesAnyRequestedRef(task, requestedTaskRefs, ''));

  return relevantExamTasks
    .map((task: any) => String(task?.taskNumber ?? ''))
    .filter((taskNumber: string) => taskNumber.length > 0);
}

// --- Grading units ---
//
// One unit is one grading CALL: a submission's main task, or a submission's language-use group. The two helpers
// below are the same pull-on-start / push-on-finish protocol the extraction steps use — see `finishedUnits`
// on `GradingProgress` for why grading in particular needed it.

/** A grading unit's id. `taskRef` is a main task number, or `languageUseUnitRef` for a group. */
export const gradingUnitId = (submissionId: string, taskRef: string): string => `${submissionId}::${taskRef}`;

/** A language-use group's ref, namespaced so a group id can never collide with a task number. */
export const languageUseUnitRef = (schemeId: string): string => `g:${schemeId}`;

/**
 * Takes units out of the graded set because they are about to be (re)graded.
 *
 * The whole of the old joining-a-session arithmetic, done exactly: those units are no longer graded, and
 * every unit this run does not touch keeps its place. Idempotent — pulling ids that are not there is free —
 * so two runs opening at once cannot corrupt each other's view.
 */
async function markGradingUnitsStarted(examID: string, unitIds: string[]): Promise<void> {
  if (!unitIds.length) return;
  const examCollection = await getCollection(Exam);
  await examCollection.updateOne({ _id: examID }, { $pullAll: { 'examState.gradingProgress.finishedUnits': unitIds } });
}

/**
 * Opens (or re-opens) the exam's grading progress run and claims `unitIds` for the caller.
 *
 * SHARED BY BOTH PHASES THAT OWN UNITS, and it has to be shared rather than left to the content run. With all
 * three phases of the wave dispatched up front, a `languageUseGroupGrading` row can be claimed before any
 * content row is — its content siblings can be held back by `subtreeGradable` while it has no gate of its own —
 * and `markGradingUnitFinished` deliberately refuses to write against a record that is not IN_PROGRESS. The
 * group's unit would then be claimed by nobody, finished by nobody, and the bar would hang one short of total
 * for the rest of the wave. Whoever gets there first opens the run instead.
 *
 * Idempotent, because none of it reads the previous values: `total` is derived from a count of what the EXAM
 * currently holds, and `$pullAll` of ids that are already gone is free.
 */
export async function openGradingProgressRun(
  examID: string,
  unitIds: string[],
  loaded?: { examTasks: any[]; languageUseSchemeRules: LanguageUseSchemeLike[] },
): Promise<void> {
  const examCollection = await getCollection(Exam);

  let examTasks: any[];
  let languageUseSchemeRules: LanguageUseSchemeLike[];
  if (loaded) {
    ({ examTasks, languageUseSchemeRules } = loaded);
  } else {
    const examDoc = await examCollection.findOne(
      { _id: examID },
      { projection: { tasks: 1, languageUseSchemeRules: 1 } } as any,
    );
    examTasks = Array.isArray((examDoc as any)?.tasks) ? (examDoc as any).tasks : [];
    languageUseSchemeRules = Array.isArray((examDoc as any)?.languageUseSchemeRules)
      ? (examDoc as any).languageUseSchemeRules
      : [];
  }

  // The counter describes the EXAM, not this run: "how much of this exam is graded". So the total is every
  // gradable unit the exam currently holds — one per (submission that has extracted answers, task) plus its
  // language-use groups — and grading one added submission raises it (10 → 15) instead of replacing it (5).
  // Language-use groups are counted independently of any run's `target`, so a content-only re-grade doesn't
  // shrink the exam's total.
  const submissionCollection = await getCollection(Submission);
  const gradableSubmissionCount = await submissionCollection.countDocuments({
    examId: examID,
    'taskSubmissions.0': { $exists: true },
  } as any);
  const examUnitCount = Math.max(
    unitIds.length,
    gradableSubmissionCount * (examTasks.length + countRelevantLanguageUseGroups(examTasks, languageUseSchemeRules)),
  );

  await examCollection.updateOne(
    { _id: examID },
    {
      $set: {
        'examState.gradingProgress.status': 'IN_PROGRESS',
        'examState.gradingProgress.message': `Bewertung läuft...`,
        'examState.gradingProgress.total': examUnitCount,
      },
    },
  );
  await markGradingUnitsStarted(examID, unitIds);
}

/**
 * Records one unit as graded.
 *
 * Also called when a unit ERRORS, which is where grading deliberately differs from answer extraction: the
 * failure is written per (submission, task) as `TaskGradingState.ERROR` on the submission itself and the
 * teacher regrades from there, so the unit is genuinely no longer outstanding. Leaving it out of the set
 * would stall the bar on a state the submission already reports in a place the teacher can act on.
 */
export async function markGradingUnitFinished(examID: string, unitId: string): Promise<void> {
  const examCollection = await getCollection(Exam);
  await examCollection.updateOne(
    { _id: examID, 'examState.gradingProgress.status': 'IN_PROGRESS' },
    { $addToSet: { 'examState.gradingProgress.finishedUnits': unitId } },
  );
}

/**
 * Groups requested task refs by the MAIN task each belongs to.
 *
 * The job unit is the main task, but a regrade's SCOPE can be finer: `['3a']` means "the pass runs over main
 * task 3, but only subtask a is regraded" (`isSubtaskLevelRequest` in `prepareRegradingRun`, `isSubtaskFilter`
 * in the grading loop). A dispatcher that only knew main task numbers would have to widen every subtask
 * regrade to its whole task — or, matching `['3a']` against main task numbers, dispatch nothing at all.
 *
 * So each main task gets its own entry carrying the refs that landed in it, and those refs travel in the job
 * payload. `refs` is empty when no scope was requested, meaning the whole main task.
 */
export async function groupTaskRefsByMainTask(
  examID: string,
  taskRefs?: string[],
): Promise<Array<{ taskKey: string; taskNumber: string; refs: string[] }>> {
  const examCol = await getCollection(Exam);
  const examDoc = await examCol.findOne({ _id: examID }, { projection: { tasks: 1 } });
  const examTasks: any[] = Array.isArray((examDoc as any)?.tasks) ? (examDoc as any).tasks : [];

  const requested = taskRefs && taskRefs.length > 0 ? taskRefs.map(String) : null;
  const out: Array<{ taskKey: string; taskNumber: string; refs: string[] }> = [];

  for (const task of examTasks) {
    const taskKey = String(task?._id ?? '');
    const taskNumber = String(task?.taskNumber ?? '');
    // A task with no `_id` cannot be addressed by a queued row across a tree rebuild.
    if (!taskKey || !taskNumber) continue;

    if (!requested) {
      out.push({ taskKey, taskNumber, refs: [] });
      continue;
    }
    // One ref at a time, so the entry records WHICH of them selected this task rather than the whole
    // request — two refs in different main tasks must not both be sent to both jobs.
    const refs = requested.filter(ref => taskMatchesAnyRequestedRef(task, toNormalizedTaskRefSet([ref]), ''));
    if (refs.length > 0) out.push({ taskKey, taskNumber, refs });
  }

  return out;
}

export async function prepareRegradingRun(
  examID: string,
  submissionIds: string[],
  taskNumbers?: string[],
): Promise<{ total: number; mainTaskNumbers: string[] }> {
  const uniqueSubmissionIds = Array.from(new Set(submissionIds.map(String))).filter(Boolean);
  const mainTaskNumbers = await resolveRelevantMainTaskNumbers(examID, taskNumbers);
  const total = uniqueSubmissionIds.length * mainTaskNumbers.length;

  const rawSubmissions = await getRawCollection(Submission._collectionName);

  if (uniqueSubmissionIds.length > 0 && mainTaskNumbers.length > 0) {
    const normalizedRequestedRefs = taskNumbers && taskNumbers.length > 0 ? toNormalizedTaskRefSet(taskNumbers) : null;
    const isSubtaskLevelRequest =
      normalizedRequestedRefs != null &&
      mainTaskNumbers.every(mainNum => !normalizedRequestedRefs.has(normalizeTaskRef(mainNum)));

    if (!isSubtaskLevelRequest) {
      await rawSubmissions.updateMany(
        { _id: { $in: uniqueSubmissionIds }, examId: examID } as any,
        {
          $set: {
            'taskSubmissions.$[main].gradingState': TaskGradingState.PENDING,
            'taskSubmissions.$[main].subTasksSubmission.$[].gradingState': TaskGradingState.PENDING,
          },
        },
        {
          arrayFilters: [{ 'main.taskNumber': { $in: mainTaskNumbers } }],
        },
      );
    } else {
      for (const submissionId of uniqueSubmissionIds) {
        const sub = await rawSubmissions.findOne({ _id: submissionId, examId: examID } as any, {
          projection: { taskSubmissions: 1 },
        });
        if (!sub?.taskSubmissions) continue;
        // The read only discovers WHICH refs match and where they sit; the write names those nodes' own fields.
        // A tree that changed in between makes the filters match nothing — a no-op, never a clobber.
        const { updateSet, arrayFilters } = collectPendingTargets(sub.taskSubmissions, normalizedRequestedRefs);
        if (Object.keys(updateSet).length === 0) continue;
        await rawSubmissions.updateOne({ _id: submissionId } as any, { $set: updateSet }, { arrayFilters });
      }
    }
  }

  return { total, mainTaskNumbers };
}

/**
 * WHAT THIS RUN DID, and what it hands back for somebody else to finish.
 *
 * IT ONLY EVER DOES PHASE ONE. Grading has three phases with different units — grading a task for a student is
 * per (task, submission), reconciling that task's grades is per TASK across submissions, and grading a
 * language-use scheme group is per (submission, GROUP) with a group spanning several tasks. This function is the
 * first, and it must not attempt the other two: it holds one student, so it has nothing to compare, and it would
 * grade a three-task group once per member task.
 *
 * There were briefly two flags for this. They are gone, because both of them only ever had one value: the sole
 * caller is `grading.job.ts` and it always defers. Two code paths where one is unreachable is worse than one.
 *
 * IT NO LONGER REPORTS THE GROUPS IT SAW, either. It used to hand back every multi-member group it passed so the
 * job could enqueue a row per (submission, group); those rows are dispatched up front now — see
 * `languageUseGroupGrading`'s `batch.units`, which enumerates the same set from the exam without needing a run to
 * discover it. Scoping a wave to one edited scheme moved there with it.
 */
export interface RunGradingAgentOutcome {
  /**
   * This run's first-pass grades in the shape the consistency reviewer reads them, for the per-task job to
   * aggregate over the wave. Always `role: 'target'` — a run's own grades are what it is asking to have checked.
   */
  consistencyItems: ConsistencyReviewInput[];
}

export async function runGradingAgent(
  examID: string,
  submissionIDOrIDs: string | string[],
  taskNumbers?: string[],
  target: GradeTaskTarget = 'content',
  // Batches a solo (1-member) LanguageUseScheme rule into the SAME 'content' call, instead of skipping
  // language-use entirely under target 'content' — see GradeTaskOptions.includeSoloLanguageUse. Only true for a
  // genuine first grade of a submission.
  includeSoloLanguageUse = false,
): Promise<RunGradingAgentOutcome> {
  const inputIDs = Array.isArray(submissionIDOrIDs) ? submissionIDOrIDs : [submissionIDOrIDs];

  logger.info(
    { examID, taskNumbers: taskNumbers?.join(', ') ?? 'all', submissionCount: inputIDs.length },
    'Starting exercise grading generation job',
  );
  const gradingStartTime = Date.now();

  const subCol = await getCollection(Submission);
  const resolvedSubDocs = await subCol
    .find({
      $or: [{ _id: { $in: inputIDs } }, { studentId: { $in: inputIDs } }],
      examId: examID,
    })
    .toArray();

  if (!resolvedSubDocs || resolvedSubDocs.length === 0) {
    throw new Error(`Keine Abgaben für die IDs ${inputIDs.join(', ')} gefunden.`);
  }

  const actualSubmissionIds = resolvedSubDocs.map(d => String(d._id));
  const examCollection = await getCollection(Exam);

  let runningCounterIncreased = false;
  try {
    // Enter the grading session and find out whether we OPENED it, in one atomic write: the guard only
    // matches when no other run is active, so exactly one concurrent run is told it is first. That
    // decides how the progress counter is initialised below — reset for the first run, extended for a
    // run that joins one already in flight — and it replaces the old `hasGradingStarted`/`total`
    // comparison, which could not tell "the exam grew" from "a second run started".
    const opened = await examCollection.updateOne(
      {
        _id: examID,
        $or: [{ 'examState.isCurrentlyGrading': { $lte: 0 } }, { 'examState.isCurrentlyGrading': null }],
      } as any,
      {
        $inc: { 'examState.isCurrentlyGrading': 1 },
        $set: { 'examState.hasGradingStarted': true },
      },
    );
    const openedSession = opened.matchedCount > 0;
    if (!openedSession) {
      await examCollection.updateOne(
        { _id: examID },
        {
          $inc: { 'examState.isCurrentlyGrading': 1 },
          $set: { 'examState.hasGradingStarted': true },
        },
      );
    }
    runningCounterIncreased = true;

    const exam = await getByID(
      Exam,
      examID,
    )({
      userID: 1,
      grammarFiSettingsJson: 1,
      languageUseSchemeRules: {
        _id: 1,
        criteria: 1,
        maxPoints: 1,
        confidence: 1,
        isApproved: 1,
        carriedOverFromGroup: 1,
        dependsOn: 1,
      },
      examState: {
        gradingProgress: {
          status: 1,
          total: 1,
          message: 1,
          _class: 1,
        },
      },
      tasks: {
        ...buildExamTaskSelection(8),
        overallGradingScheme: { examId: 1, criteria: 1, maxPoints: 1, feedback: 1 },
        overallMasterSolution: { examId: 1, content: 1, feedback: 1 },
      },
    });

    if (!exam) throw new Error(`Cannot create grading if exam wasn't found (${examID})`);

    // Per-task mode (none/fi/languageUse) is resolved per grammar target below via taskGrammarMode, since
    // different tasks/subtasks can have different detected/merged rules; a task with a configured
    // rule always uses 'languageUse', regardless of the exam/teacher alwaysUseFi default.
    const languageUseSchemeRules: LanguageUseSchemeLike[] = Array.isArray((exam as any).languageUseSchemeRules)
      ? (exam as any).languageUseSchemeRules
      : [];

    // Grammar work that must not block grading completion (locate step; FI-mode detection). The
    // FI summary is recomputed once everything has settled.
    //
    // THUNKS, NOT PROMISES, and that is what bounds them. Pushing a started promise from inside the grading loop
    // meant the model payloads accumulated across every wave and every task: by the last student, a whole cohort's
    // locate calls could be building payloads at once, so the wave bound in `gradeOneTask` was undone by the work
    // it queued. A thunk starts when `inWaves` runs it, which is what lets this job declare a measured footprint
    // (`declareGradingWaveHeap`) instead of holding its type's worst case for the whole run.
    //
    // The cost is that grammar work now starts after the grading loop rather than overlapping it. Most of that
    // overlap was queueing rather than progress — these calls compete for the same `LLM_CAPACITY` permits as the
    // grading calls ahead of them — and the FI recompute was already detached, so nothing waits on it that did not
    // wait before.
    const pendingGrammarWork: Array<() => Promise<unknown>> = [];
    /**
     * Solo (1-member) LanguageUseScheme result WRITES, awaited before this run reports done.
     *
     * Its own array rather than the one multi-member group grading used to share, because that work is a job now
     * and these are not grading at all — they are the `languageUseRuleResults` upsert that `queueSoloLanguageUseWrite`
     * defers so a language-use write can never block the content `$set` beside it.
     *
     * PROMISES, NOT THUNKS, and the distinction that matters elsewhere in this file does not apply here: `inWaves`
     * bounds how many MODEL PAYLOADS are resident at once, and these are small writes with no payload to hold.
     *
     * AWAITED, because the language-use score is part of what this row produced. Letting it land after the row
     * reports done would let the reconciliation phase and the wave's announcement read a grade whose language-use
     * half is still in flight.
     */
    const pendingSoloLanguageUseWrites: Promise<unknown>[] = [];
    // Every consistency item this run produced, across its tasks — returned so a per-task job can review the
    // whole wave at once. Collected even when this run reviews them itself, which costs an array and keeps one
    // return shape for both callers.
    const collectedConsistencyItemsThisRun: ConsistencyReviewInput[] = [];

    // Resolve the language once per run for the grammar evaluation (LanguageTool needs a language
    // code). Mirrors how gradeTask resolves the user language from the conversation.
    const grammarLanguageCode = 'auto';
    // try {
    //   const conversationCollection = await getCollection(Conversation);
    //   const conversation = await conversationCollection.findOne({ examId: examID });
    //   grammarLanguageCode = normalizeLanguageCode((conversation as any)?.language);
    // } catch (langErr) {
    //   logger.warn({ err: langErr, examID, context: 'GRAMMAR' }, 'Failed to resolve language for grammar eval; defaulting to de-DE');
    // }

    const rawSubmissions = await Promise.all(
      actualSubmissionIds.map(id =>
        getByID(
          Submission,
          id,
        )({
          _id: 1,
          examId: 1,
          studentId: 1,
          imageIds: 1,
          taskSubmissions: {
            ...buildSubmissionTaskSelection(8),
          },
        }),
      ),
    );

    const validSubmissions = rawSubmissions.filter((s): s is NonNullable<typeof s> => s !== null);

    const examTasks = exam.tasks ?? [];
    const requestedTaskRefs = taskNumbers && taskNumbers.length > 0 ? toNormalizedTaskRefSet(taskNumbers) : null;

    const relevantExamTasks =
      requestedTaskRefs && requestedTaskRefs.size > 0
        ? examTasks.filter(task => taskMatchesAnyRequestedRef(task, requestedTaskRefs, ''))
        : examTasks;

    if (relevantExamTasks.length === 0) {
      logger.warn({ examID }, 'Keine Aufgaben zum Bewerten gefunden');
      return { consistencyItems: [] };
    }

    // NOTHING TO ARM HERE ANY MORE, and nothing to declare. There is no
    // heap accounting to arm: the reservation system that measured each payload against a per-job ceiling is gone.
    // What remains, and what actually mattered, is keeping the fan-out at `GRADING_WAVE` — which `inWaves` does.
    //
    // A worst-case ESTIMATE was tried in front of the swap and removed: walking every task and submission for image
    // ids and reading their sizes bought one pessimistic figure — the heaviest task meeting the heaviest student,
    // which never happens — at the cost of a full tree traversal plus a query per run. The swap reaches a better
    // number from the payloads themselves within the first wave, so the estimate only paid for the seconds before it.
    // Multi-member language-use-group grading is a JOB of its own now, and it still has to count toward
    // total/finished — otherwise finished reaches total as soon as the (fast) per-task content loop ends,
    // showing "x/x" while a `languageUseGroupGrading` row is still running. One unit per (submission, group),
    // matching how it is actually graded: a group depending on several tasks is a single grading call, not one
    // per member task. Solo rules add no unit, because they are graded inside their task's call and are already
    // counted by it. `openGradingProgressRun` counts those group units target-independently, so the TOTAL is
    // unaffected by this run's scope.
    //
    // THIS RUN CLAIMS ONLY ITS OWN TASK UNITS. A group unit is claimed and released by the group's own row (see
    // `runLanguageUseGroupForSubmission`), because the two no longer run in a fixed order: once a group row can
    // finish before a sibling content row starts, a content run that also pulled that group's unit would take a
    // finished unit back out of the set with nothing left to put it back, and the bar would hang one short of
    // total forever.
    //
    // Exactly which (submission, task) pairs this run is about to redo. This list IS the run's claim on the
    // progress state — see `markGradingUnitsStarted`, which is the whole of what the old counter corrections
    // were trying to say.
    const unitIdsThisRun = validSubmissions.flatMap(submission =>
      relevantExamTasks.map(task => gradingUnitId(submission._id, task.taskNumber)),
    );

    // Opening the run is the same write whether or not this process opened the SESSION, because none of it
    // depends on what the numbers were before, and the units this run will redo are pulled out of the graded
    // set by id.
    //
    // That pull replaces the arithmetic this used to need. Resuming where the bar stood (15 units, 5 in this
    // run → 10/15 climbing back to 15/15) used to be `finished = total - unitsThisRun`, which only worked if
    // every unit outside the run was already graded — an assumption stated in the code and covered for by
    // forcing `finished = total` at close. Joining a live session needed a second, different correction
    // (`$max(0, finished - unitsThisRun)`) so two concurrent runs could not drive the bar negative. Removing
    // exactly this run's ids is both cases, exactly, and two runs pulling at once cannot interfere.
    await openGradingProgressRun(examID, unitIdsThisRun, { examTasks, languageUseSchemeRules });

    const rawCollection = await getRawCollection(Submission._collectionName);

    // --- grade one task across all submissions (the per-task body; driven by the reactive loop below) ---
    const gradeOneTask = async (examTask: any): Promise<void> => {
      logger.info(
        { taskNumber: examTask.taskNumber, studentCount: validSubmissions.length },
        'Starte Bewertung für Aufgabe',
      );

      await examCollection.updateOne(
        { _id: examID },
        {
          $set: {
            'examState.gradingProgress.message': `Bewerte Aufgabe ${examTask.taskNumber} ...`,
          },
        },
      );

      // --- student loop ---
      // A FACTORY, not an array of running promises. `validSubmissions.map(async …)` invoked every branch
      // immediately, so the fan-out was already in flight before anything could bound it — slicing the resulting
      // array would have bounded only when results were collected, not when payloads were built.
      const gradeStudent = async (submission: (typeof validSubmissions)[number]) => {
        const submissionId = String(submission._id);
        const submissionTaskMap = new Map<string, SubmissionTask>(
          (submission.taskSubmissions ?? []).map(st => [st.taskNumber, st as SubmissionTask]),
        );

        const submissionTask = submissionTaskMap.get(examTask.taskNumber);
        if (!submissionTask) return null;

        const isSubtaskFilter =
          requestedTaskRefs != null &&
          requestedTaskRefs.size > 0 &&
          !requestedTaskRefs.has(normalizeTaskRef(examTask.taskNumber));

        const setProcessingUpdate: any = {
          'taskSubmissions.$[main].gradingState': TaskGradingState.PROCESSING,
        };

        if (!isSubtaskFilter && submissionTask.subTasksSubmission && submissionTask.subTasksSubmission.length > 0) {
          setProcessingUpdate['taskSubmissions.$[main].subTasksSubmission.$[].gradingState'] =
            TaskGradingState.PROCESSING;
        }

        await rawCollection.updateOne(
          { _id: submissionId } as any,
          { $set: setProcessingUpdate },
          { arrayFilters: [{ 'main.taskNumber': examTask.taskNumber }] },
        );

        try {
          // Grammar evaluation runs BEFORE grading. A SOLO rule awaits detection so the error list can be
          // injected into the SAME grading call — no dedicated LLM call for it; FI mode stays
          // fully fire-and-forget (applies downstream, doesn't depend on detected errors). The locate
          // step (on-image boxes) is always fire-and-forget and skipped on reuse. Grammar-eligible
          // nodes are collected recursively: a combined main task can itself be a purely structural
          // type (e.g. TASK_FRAME) while its subtasks carry the real essay/poetry type (or carry a
          // merged/grouped rule directly), so eligibility is checked at every depth. Own-criteria and
          // group membership are NOT mutually exclusive — a task can have its own rule AND belong to
          // one or more groups at once; both are graded independently (see gradingAgent phase-2 plan).
          // target 'content' skips grammar entirely for this run — no detection, no group grading — unless
          // includeSoloLanguageUse asks for a genuine first grade to batch a solo rule into this same call
          // (and dispatch any multi-member group this run touches).
          // collectGrammarTargets walks examTask's WHOLE subtree, which can span several unrelated
          // LanguageUseScheme groups sharing the same top-level main task (relevantExamTasks includes a main
          // task as soon as ANY of its descendants is requested). Filtered by requestedTaskRefs the same
          // way gradeTask's content path already is (via its own requestedTaskRefs param below) — without
          // this, regrading one member of group B also silently regrades unrelated group A just because
          // both happen to nest under the same main task.
          const grammarTargets =
            target === 'content' && !includeSoloLanguageUse
              ? []
              : collectGrammarTargets(
                  examTask,
                  submissionTask,
                  [],
                  String(examTask.taskNumber),
                  languageUseSchemeRules,
                ).filter(t => qualifiedPathMatchesRequestedRefs(t.qualifiedTaskNumber, requestedTaskRefs));
          const errorsByTaskId: Record<string, DetectedGrammarError[]> = {};

          if (grammarTargets.length > 0) {
            await Promise.all(
              grammarTargets.map(async grammarTarget => {
                const targetTaskNumber = grammarTarget.qualifiedTaskNumber;
                const mode = taskGrammarMode(grammarTarget.examNode, targetTaskNumber, languageUseSchemeRules);
                // Only multi-member groups get a dedicated `gradeLanguageUseSchemeGroup` call, and they get it
                // from a `languageUseGroupGrading` row dispatched up front — nothing here has to find them. A
                // 1-member rule is graded inline via `soloRule` below, exactly like the old own-criteria path, so
                // its LLM call is batched for free into the task's content call instead of costing a dedicated one.
                const soloRule =
                  mode === 'languageUse' ? getSoloLanguageUseRule(targetTaskNumber, languageUseSchemeRules) : null;

                const grammarArgs: RunGrammarEvaluationArgs = {
                  examID,
                  submissionId,
                  examTask: grammarTarget.examNode as ExamTask,
                  submissionTask: grammarTarget.submissionNode,
                  languageCode: grammarLanguageCode,
                  submissionImageIds: (submission as any).imageIds ?? [],
                  fieldPath: grammarTarget.fieldPath,
                  arrayFilters: grammarTarget.arrayFilters,
                };

                if (soloRule) {
                  const detection = await detectGrammarErrorsForTask(grammarArgs);
                  if (!detection.reused) {
                    pendingGrammarWork.push(() =>
                      locateGrammarErrorsForTask(grammarArgs, detection).catch(err =>
                        logger.error(
                          { err, examID, submissionId, taskNumber: targetTaskNumber, context: 'GRAMMAR' },
                          'Grammar locate step failed (non-fatal)',
                        ),
                      ),
                    );
                  }
                  errorsByTaskId[String((grammarTarget.examNode as any)?._id || '')] = detection.errors;
                } else {
                  pendingGrammarWork.push(() =>
                    detectGrammarErrorsForTask(grammarArgs)
                      .then(detection =>
                        detection.reused ? undefined : locateGrammarErrorsForTask(grammarArgs, detection),
                      )
                      .catch(err =>
                        logger.error(
                          { err, examID, submissionId, taskNumber: targetTaskNumber, context: 'GRAMMAR' },
                          'Grammar evaluation failed (non-fatal)',
                        ),
                      ),
                  );
                }

              }),
            );
          }

          const gradings = await gradeTask(examTask as ExamTask, submissionTask, {
            requestedTaskRefs,
            examId: examID,
            target,
            languageUseSchemeRules,
            includeSoloLanguageUse,
            ...(Object.keys(errorsByTaskId).length > 0 ? { grammar: { errorsByTaskId } } : {}),
          });

          const typedGradings = gradings as Array<
            Grading & {
              taskType: string;
              mainTaskNumber: string;
              subTaskNumber?: string;
              maxPoints?: number;
              dbId: string;
              mainDbId: string;
              nestingPath?: string[];
            }
          >;

          const taskAny = examTask as any;

          const mainGrading = typedGradings.find(g => g.taskType === 'MAIN');
          const subGradings = typedGradings.filter(g => g.taskType === 'SUB');

          if (!mainGrading && taskAny.needsAnswer !== false && target !== 'languageUse') {
            logger.warn(
              {
                examID,
                submissionId,
                studentId: submission.studentId,
                mainTaskNumber: examTask.taskNumber,
                needsAnswer: taskAny.needsAnswer !== false,
                returnedTypes: typedGradings.map(g => g.taskType),
                returnedAnswerIds: typedGradings.map(g => g.answerID),
                returnedDbIds: typedGradings.map(g => g.dbId),
                context: 'runGradingAgent',
              },
              'Missing MAIN grading from LLM result. Creating fallback gradingResult with score=0',
            );
          }

          const updateSet: any = {};
          const arrayFilters: any[] = [{ 'main.taskNumber': examTask.taskNumber }];

          let aggregatedSubScore = 0;
          const allConfidences: number[] = [];
          const allExplanations: string[] = [];
          const allFeedbacks: string[] = [];
          // Collected for the post-loop consistency-review pass (see below Promise.all) — one entry per
          // graded node for this (task, submission) pair, skipped entirely for language-use-only runs.
          const consistencyItems: ConsistencyReviewInput[] = [];

          // --- save subtask (including deeply nested ones) ---
          for (const [gradingIndex, grading] of subGradings.entries()) {
            // `nestingPath` (built in default-grading.ts's walkTask) is already relative to the main
            // task — every segment is an ancestor's own bare taskNumber, never a redundant repeat of
            // examTask.taskNumber. Stripping a leading segment that happens to match it would wrongly
            // drop a REAL segment whenever an intermediate node's bare label coincides with the main
            // task's (e.g. a sub-question label that restarts at "1" inside a task frame).
            const relativeNestingPath = grading.nestingPath || [];
            const nestedTaskDef = findTaskByPath(examTask, relativeNestingPath);
            const nestedMaxPoints = ownMaxPoints(nestedTaskDef);

            if (relativeNestingPath.length === 0) {
              // First-level subtask (backward compatibility)
              const subNumber = grading.subTaskNumber || grading.answerID;
              const subTaskDef = examTask.subTasks?.find(st => st.taskNumber === subNumber);
              const subMaxPoints = ownMaxPoints(subTaskDef);
              const safeSubScore = clampScore(grading.grade, subMaxPoints);

              aggregatedSubScore += safeSubScore;
              allConfidences.push(grading.confidence || 1);
              allExplanations.push(`Teilaufgabe ${subNumber}: ${grading.explanation}`);
              if (grading.reviewerFeedback) allFeedbacks.push(`Teilaufgabe ${subNumber}: ${grading.reviewerFeedback}`);

              const safeSubId = `sub${subNumber.replace(/[^a-zA-Z0-9]/g, '')}`;
              arrayFilters.push({ [`${safeSubId}.taskNumber`]: subNumber });

              const existingSubNode = (submissionTask.subTasksSubmission as any[] | undefined)?.find(
                s => String(s?.taskNumber) === String(subNumber),
              );
              updateSet[`taskSubmissions.$[main].subTasksSubmission.$[${safeSubId}].gradingResult`] =
                buildMergedGradingResult(
                  existingSubNode?.gradingResult,
                  target === 'languageUse'
                    ? null
                    : {
                        score: safeSubScore,
                        explanation: grading.explanation,
                        confidence: String(grading.confidence || 1),
                        gradingSource: 'ai_first_pass',
                        ...(grading.reviewerFeedback ? { reviewerFeedback: grading.reviewerFeedback } : {}),
                      },
                );
              queueSoloLanguageUseWrite(pendingSoloLanguageUseWrites, submissionId, grading, examID);

              updateSet[`taskSubmissions.$[main].subTasksSubmission.$[${safeSubId}].gradingState`] =
                TaskGradingState.COMPLETED;

              if (target !== 'languageUse') {
                consistencyItems.push({
                  submissionId,
                  dbId: grading.dbId,
                  nodeLabel: subNumber,
                  criteria: String((subTaskDef as any)?.gradingScheme?.criteria ?? ''),
                  score: safeSubScore,
                  maxPoints: subMaxPoints,
                  explanation: grading.explanation,
                  studentAnswerExcerpt: String((submissionTask as any)?.studentAnswer ?? ''),
                  gradingResultFieldPath: `taskSubmissions.$[main].subTasksSubmission.$[${safeSubId}].gradingResult`,
                  arrayFilters: [
                    { 'main.taskNumber': examTask.taskNumber },
                    { [`${safeSubId}.taskNumber`]: subNumber },
                  ],
                });
              }
            } else {
              // Deep nested subtask (2+ levels)
              const { path, filters } = buildNestedUpdatePath(relativeNestingPath, `d${gradingIndex}n`);

              // Add new filters for this deep path
              arrayFilters.push(...filters);

              const existingDeepNode = findSubmissionNodeByPath(submissionTask, relativeNestingPath);
              updateSet[`${path}.gradingResult`] = buildMergedGradingResult(
                existingDeepNode?.gradingResult,
                target === 'languageUse'
                  ? null
                  : {
                      score: clampScore(grading.grade, nestedMaxPoints),
                      explanation: grading.explanation,
                      confidence: String(grading.confidence || 1),
                      gradingSource: 'ai_first_pass',
                      ...(grading.reviewerFeedback ? { reviewerFeedback: grading.reviewerFeedback } : {}),
                    },
              );
              queueSoloLanguageUseWrite(pendingSoloLanguageUseWrites, submissionId, grading, examID);

              updateSet[`${path}.gradingState`] = TaskGradingState.COMPLETED;

              allConfidences.push(grading.confidence || 1);
              const taskPath = relativeNestingPath.join('.');
              allExplanations.push(`Aufgabe ${taskPath}: ${grading.explanation}`);
              if (grading.reviewerFeedback) allFeedbacks.push(`Aufgabe ${taskPath}: ${grading.reviewerFeedback}`);

              if (target !== 'languageUse') {
                consistencyItems.push({
                  submissionId,
                  dbId: grading.dbId,
                  nodeLabel: taskPath,
                  criteria: String((nestedTaskDef as any)?.gradingScheme?.criteria ?? ''),
                  score: clampScore(grading.grade, nestedMaxPoints),
                  maxPoints: nestedMaxPoints,
                  explanation: grading.explanation,
                  studentAnswerExcerpt: String(
                    existingDeepNode?.studentAnswer ?? (submissionTask as any)?.studentAnswer ?? '',
                  ),
                  gradingResultFieldPath: `${path}.gradingResult`,
                  arrayFilters: [{ 'main.taskNumber': examTask.taskNumber }, ...filters],
                });
              }
            }
          }

          const mainNeedsAnswer = taskAny.needsAnswer !== false;
          // The main task's OWN points from the single source of truth — its gradingResult holds only its own
          // answer's score (children are written separately), so clamp to own, not the subtree total. 0 when
          // unpointed; no default-to-1.
          const mainMaxPoints = ownMaxPoints(examTask);
          const prevMainPoints = submissionTask.gradingResult?.actualPointsGiven;

          let finalMainScore = 0;
          let finalExplanations = '';
          let finalFeedbacks = '';

          if (mainGrading) {
            const safeMainScore = clampScore(mainGrading.grade, mainMaxPoints);
            finalMainScore = clampScore(safeMainScore, mainMaxPoints);

            allConfidences.push(mainGrading.confidence || 1);
            finalExplanations = mainGrading.explanation;
            finalFeedbacks = [mainGrading.reviewerFeedback ? mainGrading.reviewerFeedback : '']
              .filter(Boolean)
              .join('\n\n');
          }
          // i think we dont need the aggregated gradingResult, could cause some problems
          // else {
          //   finalMainScore = clampScore(aggregatedSubScore, mainMaxPoints);
          //   finalExplanations = allExplanations.join('\n\n');
          //   finalFeedbacks = allFeedbacks.join('\n\n');
          // }

          if (mainGrading) {
            updateSet['taskSubmissions.$[main].gradingResult'] = buildMergedGradingResult(
              submissionTask.gradingResult,
              target === 'languageUse'
                ? null
                : {
                    score: finalMainScore,
                    explanation: finalExplanations,
                    ...(finalFeedbacks ? { reviewerFeedback: finalFeedbacks } : {}),
                    confidence: String(mainGrading.confidence || 1),
                    gradingSource: 'ai_first_pass',
                    ...(prevMainPoints !== undefined ? { actualPointsGiven: prevMainPoints } : {}),
                  },
            );
            queueSoloLanguageUseWrite(pendingSoloLanguageUseWrites, submissionId, mainGrading, examID);

            if (target !== 'languageUse') {
              consistencyItems.push({
                submissionId,
                dbId: mainGrading.dbId,
                nodeLabel: examTask.taskNumber,
                criteria: String(taskAny?.gradingScheme?.criteria ?? ''),
                score: finalMainScore,
                maxPoints: mainMaxPoints,
                explanation: finalExplanations,
                studentAnswerExcerpt: String((submissionTask as any)?.studentAnswer ?? ''),
                gradingResultFieldPath: 'taskSubmissions.$[main].gradingResult',
                arrayFilters: [{ 'main.taskNumber': examTask.taskNumber }],
              });
            }
          } else if (mainNeedsAnswer && target !== 'languageUse') {
            updateSet['taskSubmissions.$[main].gradingResult'] = buildMergedGradingResult(
              submissionTask.gradingResult,
              {
                score: 0,
                explanation: 'Fehler bei der Bewertung',
                confidence: '0',
                reviewerFeedback:
                  'Fallback gesetzt: LLM lieferte kein MAIN-Ergebnis für diese Aufgabe; Ergebnis wurde mit 0 Punkten initialisiert.',
                ...(prevMainPoints !== undefined ? { actualPointsGiven: prevMainPoints } : {}),
              },
            );
          }

          updateSet['taskSubmissions.$[main].gradingState'] = TaskGradingState.COMPLETED;
          await rawCollection.updateOne({ _id: submissionId } as any, { $set: updateSet }, { arrayFilters });

          await markGradingUnitFinished(examID, gradingUnitId(submissionId, examTask.taskNumber));

          return { gradings, consistencyItems };
        } catch (error: any) {
          logger.error(
            { err: error, taskNumber: examTask.taskNumber, submissionId },
            'Fehler beim Graden von Task für Schüler',
          );

          const setErrorUpdate: any = {
            'taskSubmissions.$[main].gradingState': TaskGradingState.ERROR,
          };
          if (!isSubtaskFilter && submissionTask.subTasksSubmission && submissionTask.subTasksSubmission.length > 0) {
            setErrorUpdate['taskSubmissions.$[main].subTasksSubmission.$[].gradingState'] = TaskGradingState.ERROR;
          }

          await rawCollection.updateOne(
            { _id: submissionId } as any,
            { $set: setErrorUpdate },
            { arrayFilters: [{ 'main.taskNumber': examTask.taskNumber }] },
          );

          // Errored units count as no longer outstanding — see `markGradingUnitFinished`.
          await markGradingUnitFinished(examID, gradingUnitId(submissionId, examTask.taskNumber));

          return null;
        }
      };

      // BOUNDED FAN-OUT. `studentPromises` was handed straight to `Promise.all`, which starts every branch at
      // once — a forty-student task built forty model payloads simultaneously, and since payloads are assembled
      // BEFORE the model call the LLM gate throttled the calls while all forty sat in the heap. `Promise.all` has
      // no concurrency control of its own; the bound has to be applied to when branches START.
      //
      // This is also what makes `grading`'s heap claim honest rather than merely conservative: the job is admitted
      // bounded by `GRADING_WAVE`, which is the width this agent chose for exactly
      // that figure, so the peak is inside the claim for a cohort of any size.
      //
      // The review pass below is unaffected: it reads `taskResults`, and every student still completes before
      // this line returns. Bounding changes when a branch starts, not whether all of them finish first — which is
      // what the cross-student consistency reconciliation depends on.
      const taskResults: Array<Awaited<ReturnType<typeof gradeStudent>>> = [];
      for (let from = 0; from < validSubmissions.length; from += GRADING_WAVE) {
        const wave = validSubmissions.slice(from, from + GRADING_WAVE);
        // eslint-disable-next-line no-await-in-loop -- sequential waves ARE the bound; that is the point
        taskResults.push(...(await Promise.all(wave.map(submission => gradeStudent(submission)))));
      }

      // --- the consistency items, collected and handed back ---
      // NOT REVIEWED HERE. This run holds ONE submission, so every group would have a single entry and
      // `comparableGroups` (which needs two) would find nothing to compare — the review is not merely wasteful at
      // this granularity, it is impossible. `gradingConsistency` reviews the whole wave for this task, with the rest
      // of the cohort supplied as read-only references.
      if (target !== 'languageUse') {
        // TRUNCATED AS IT IS COLLECTED, because this list is PERSISTED to `JobDoc.result` and then read back by the
        // per-task reconciliation. Stored in full it is the student's entire answer, per graded node, per row — tens
        // of megabytes for a cohort, all of it read again to build one prompt that slices every entry to
        // `CONSISTENCY_REVIEW_ANSWER_EXCERPT_LIMIT` anyway. It is called an excerpt; this makes it one.
        collectedConsistencyItemsThisRun.push(
          ...taskResults
            .flatMap(r => r?.consistencyItems ?? [])
            .map(item => ({
              ...item,
              studentAnswerExcerpt: item.studentAnswerExcerpt.slice(0, STORED_ANSWER_EXCERPT_LIMIT),
            })),
        );
      }
    };

    // --- reactive task loop ---
    // Grade each relevant task as soon as its master solution AND grading scheme are available,
    // re-fetching fresh content each pass. This lets grading begin while MS/GS are still being
    // generated/extracted and process tasks incrementally as they land. Bounded by both MS/GS
    // sources reaching a terminal state (COMPLETED/ERROR) and by an overall timeout, so a caller
    // awaiting completion never hangs on a task whose content never arrives. When MS/GS are already
    // done (the normal post-setup case), the first pass grades everything immediately.
    const isTerminal = (status?: StepState) => status === StepState.COMPLETED || status === StepState.ERROR;
    const isTaskReadyForGrading = (node: any): boolean => {
      if (node?.needsAnswer !== false && (!hasMasterSolutionContent(node) || !hasGradingSchemeContent(node))) {
        return false;
      }
      const subTasks = Array.isArray(node?.subTasks) ? node.subTasks : [];
      return subTasks.every(isTaskReadyForGrading);
    };

    const gradedTaskNumbers = new Set<string>();
    const waitDeadline = Date.now() + GRADING_WAIT_TIMEOUT_MS;
    while (gradedTaskNumbers.size < relevantExamTasks.length) {
      const freshExam = await getByID(
        Exam,
        examID,
      )({
        examState: {
          masterSolutionProgress: { status: 1 },
          gradingSchemeProgress: { status: 1 },
        },
        tasks: {
          ...buildExamTaskSelection(8),
          overallGradingScheme: { examId: 1, criteria: 1, maxPoints: 1, feedback: 1 },
          overallMasterSolution: { examId: 1, content: 1, feedback: 1 },
        },
      });
      const freshState = freshExam?.examState;
      const freshByNumber = new Map<string, any>(
        (freshExam?.tasks ?? []).map(t => [String((t as any).taskNumber), t as any] as [string, any]),
      );
      const sourcesSettled =
        isTerminal(freshState?.masterSolutionProgress?.status) && isTerminal(freshState?.gradingSchemeProgress?.status);
      const force = sourcesSettled || Date.now() > waitDeadline;

      for (const examTask of relevantExamTasks) {
        const taskNumber = String(examTask.taskNumber);
        if (gradedTaskNumbers.has(taskNumber)) continue;
        const freshTask = freshByNumber.get(taskNumber) ?? examTask;
        if (!force && !isTaskReadyForGrading(freshTask)) continue;
        gradedTaskNumbers.add(taskNumber);
        await gradeOneTask(freshTask);
      }

      if (gradedTaskNumbers.size >= relevantExamTasks.length || force) break;
      await new Promise(resolveDelay => setTimeout(resolveDelay, GRADING_WAIT_POLL_MS));
    }

    logger.info(
      {
        examID,
        submissionCount: validSubmissions.length,
        taskCount: relevantExamTasks.length,
        durationMs: Date.now() - gradingStartTime,
      },
      'Grading completed for exam',
    );

    // Recompute the per-student FI summaries once all detached grammar work has settled. Chained
    // fire-and-forget so grading completion is never delayed by the grammar backend.
    void inWaves(pendingGrammarWork)
      .then(() => persistGrammarFiOutcomes(examID))
      .catch(err =>
        logger.error({ err, examID, context: 'GRAMMAR_FI' }, 'Failed to persist grammar FI outcomes after grading'),
      );

    // Never rejects — `queueSoloLanguageUseWrite` catches per write, so this only waits.
    await Promise.allSettled(pendingSoloLanguageUseWrites);

    return { consistencyItems: collectedConsistencyItemsThisRun };
  } finally {
    if (runningCounterIncreased) {
      await closeGradingSession(examID);
    }
  }
}

/**
 * Leaves the grading session and, if this was the LAST run in it, marks the progress COMPLETED.
 *
 * Completion must not be announced per run: every `finished` increment is guarded on
 * `status: 'IN_PROGRESS'`, so a run that flipped the status while a second run was still grading silently
 * froze that run's counter. Only the run that leaves an empty session closes it.
 *
 * It also reconciles `total` with what was actually graded. A unit can legitimately never be graded — a
 * submission whose `taskSubmissions` don't contain one of the exam's tasks is skipped — and the bar must not
 * sit below 100% once grading is genuinely over. The mismatch is logged rather than hidden, since a large
 * gap means the estimate counted units the exam never had.
 */
async function closeGradingSession(examID: string): Promise<void> {
  const examCollection = await getCollection(Exam);
  try {
    await examCollection.updateOne(
      { _id: examID } as any,
      {
        $inc: { 'examState.isCurrentlyGrading': -1 },
      } as any,
    );

    const exam = await examCollection.findOne({ _id: examID } as any, { projection: { examState: 1 } });
    const state = (exam as any)?.examState;
    const running = Number(state?.isCurrentlyGrading ?? 0);
    if (running > 0) return;
    // A negative count would make every later run believe it opened the session. It can only come from an
    // unbalanced decrement, so clamp instead of trusting it.
    if (running < 0) {
      logger.info({ examID, running }, 'Grading run counter went negative — clamping to 0');
      await examCollection.updateOne({ _id: examID }, { $set: { 'examState.isCurrentlyGrading': 0 } });
    }

    // THE QUEUE OUTLIVES THE REFCOUNT. When grading is dispatched as one job per main task, a run's later
    // jobs may not have STARTED yet when its first one ends — the refcount is legitimately zero while two
    // rows sit pending, and marking the exam graded here would be undone by the next job reopening it.
    //
    // Asked of the ROWS, not of the record: grading is open exactly while one `grading` row is still pending or
    // claimed. Inside a job this always finds at least its own claimed row and therefore always defers, which is
    // right — the row's `onTransition` fires after the write and closes it from there. Outside one (a direct
    // call, the legacy paths) it finds nothing and closes here, as it always did.
    // THE WHOLE STEP, not just `grading`. A wave's grading rows finish before its `gradingConsistency` and
    // `languageUseGroupGrading` rows even start, and both of those WRITE GRADES — marking the exam graded between
    // them would show a finished bar over grades the next phase is about to change, and `markGradingUnitFinished`
    // is guarded on `IN_PROGRESS`, so the language-use group's own unit would then never be recorded.
    const { anyLiveJobForTypes } = await import('../jobs/job-store.js');
    const { stepTypesOf } = await import('../jobs/job.js');
    if (await anyLiveJobForTypes(examID, stepTypesOf('grading'))) {
      logger.info({ examID }, 'Grading session idle but its run is not finished');
      return;
    }

    await finishGradingRun(examID, state);
  } catch (error) {
    logger.error({ err: error, examID }, 'Failed to close grading session for exam');
  }
}

/**
 * The terminal write of a grading run: the exam is graded.
 *
 * Split out of `closeGradingSession` because there are now two moments it can be reached from — the last
 * `runGradingAgent` call of a direct grading, and the last task-JOB of a dispatched run — and only the
 * caller knows which. Idempotent: both writes are constants derived from the graded set.
 *
 * RECONCILES THE ESTIMATE DOWN TO THE SET, not the other way round. `total` is a cartesian product
 * (gradable submissions × tasks) and over-counts whenever a submission is missing one of the exam's tasks —
 * that unit is skipped without ever being graded, which is the gap this always had to close. It used to
 * close it by writing `finished = total`, i.e. by claiming units that never ran. The set cannot be lied to
 * that way and does not need to be: what was actually graded is known exactly, so the estimate is the thing
 * that was wrong and the estimate is what gets corrected.
 */
export async function finishGradingRun(examID: string, knownState?: any): Promise<void> {
  const examCollection = await getCollection(Exam);
  let state = knownState;
  if (!state) {
    const exam = await examCollection.findOne({ _id: examID }, { projection: { examState: 1 } });
    state = exam?.examState;
  }

  const total = Number(state?.gradingProgress?.total ?? 0);
  const graded = Array.isArray(state?.gradingProgress?.finishedUnits)
    ? (state.gradingProgress.finishedUnits as string[]).length
    : 0;
  if (graded !== total) {
    logger.info({ examID, total, graded }, 'Grading run closed with fewer graded units than estimated');
  }

  await examCollection.updateOne(
    { _id: examID },
    {
      $set: {
        'examState.gradingProgress.status': 'COMPLETED',
        'examState.gradingProgress.total': graded,
        'examState.gradingProgress.message': `Bewertung abgeschlossen`,
      },
    },
  );
}

/**
 * Regrade one or more main tasks for every submission. Plural because a multi-task LanguageUseScheme
 * group can span several main tasks (a language-use-only regrade of one member regrades the whole
 * group — see runGradingAgent) — regradeTaskForAllSubmissions is a thin single-task wrapper below.
 */
export async function regradeTaskNumbersForAllSubmissions(
  examId: string,
  taskNumbers: string[],
  target: GradeTaskTarget = 'content',
  // Scopes the regrade to ONE LanguageUseScheme — see runGradingAgent's own param doc. Omit for a full
  // regrade of every group these tasks belong to (e.g. a membership change affecting several schemes).
  languageUseSchemeId?: string,
): Promise<number> {
  logger.info(
    { examId, taskNumbers: taskNumbers.join(', '), target, languageUseSchemeId },
    'regradeTaskNumbersForAllSubmissions called',
  );
  try {
    const subCol = await getCollection(Submission);
    const submissions = await subCol.find({ examId }).toArray();
    if (!submissions.length) {
      logger.warn({ examId }, 'Keine Schülerabgaben für Klausur gefunden. Überspringe Regrading');
      return 0;
    }

    // Dispatched as one job per main task rather than run inline. The awaited shape is unchanged for the
    // callers (canvas edits fire this detached and never looked at the result anyway) — what changes is that
    // the grading survives a restart and shows up in the queue instead of living in a floating promise.
    // `prepareRegradingRun` moved into the job, where a requeue re-runs it.
    // THE WHOLE WAVE. A `target: 'languageUse'` regrade from here would otherwise grade no group at all: the
    // group rows are dispatched up front now rather than enqueued by the content run that finds them.
    const { waitForJobs } = await import('../jobs/dispatch.js');
    const { dispatchGradingWave } = await import('../jobs/dispatch-grading.js');
    const { jobIds } = await dispatchGradingWave(examId, {
      taskRefs: taskNumbers,
      target,
      ...(languageUseSchemeId ? { languageUseSchemeId } : {}),
    });
    await waitForJobs('grading', jobIds, examId);

    return submissions.length;
  } catch (error) {
    logger.error({ err: error, examId }, 'Fehler beim Abrufen der Schülerabgaben für Klausur');
    return 0;
  }
}

export async function regradeTaskForAllSubmissions(
  examId: string,
  taskNumber: string,
  target: GradeTaskTarget = 'content',
): Promise<number> {
  return regradeTaskNumbersForAllSubmissions(examId, [taskNumber], target);
}
